import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_tareas";
const DB_VERSION = 3;
const STORE_TAREAS = "tareas";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`: watermark por cliente, no uno global). */
export type GrpcTareaItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB **nueva y separada** para las tareas de rastro que trae
 *  `TareaGrpcService` (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web) — mismo patrón que `GrpcLoteDatabase`, delta real por
 *  `clienteId` (una sola vez por sesión: la app opera con un solo cliente
 *  logueado, pero se guarda igual con el mismo criterio que el resto). */
class GrpcTareaDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_TAREAS, clave: "id" },
      { nombre: STORE_SYNC, clave: "clienteId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(clienteId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ clienteId: string; ultimaSincronizacion: string } | undefined>(
      store.get(clienteId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcTareaItem>(
    clienteId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_TAREAS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_TAREAS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ clienteId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Todas las tareas guardadas (globales + del cliente logueado: el server
   *  ya resuelve esa unión, acá no hace falta filtrar por índice). */
  async getListAsync<T>(): Promise<T[]> {
    const db = await this.open();
    const store = db.transaction(STORE_TAREAS, "readonly").objectStore(STORE_TAREAS);
    return reqToPromise<T[]>(store.getAll());
  }

  /** Actualiza (o inserta) una sola tarea sin tocar el resto. */
  async putOneAsync<T extends GrpcTareaItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_TAREAS, "readwrite");
    tx.objectStore(STORE_TAREAS).put(item);
    await txDone(tx);
  }

  /** Borra una tarea puntual (baja confirmada por el server). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_TAREAS, "readwrite");
    tx.objectStore(STORE_TAREAS).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de tareas de rastro por gRPC-Web (con delta por
 *  cliente). */
export const grpcTareaDatabase = new GrpcTareaDatabase();
